import { Reveal, SectionTag } from "./shared";
import { useLang } from "./i18n";

const COPY = {
  en: {
    tag: "The Method",
    headA: "Five chapters. One story —",
    headB: "yours.",
    chapters: [
      { num: "01", word: "Discover", color: "#1F5BFF", copy: "A calibrated battery of aptitude tests surfaces what you're genuinely good at — logical, verbal, spatial, numerical and mechanical reasoning, measured against thousands of peers.", tags: ["Aptitude battery", "Adaptive testing"] },
      { num: "02", word: "Understand", color: "#7C3AED", copy: "Personality and emotional-intelligence profiling explains how you work — how you focus, decide, collaborate and respond under pressure. No labels, just language for who you are.", tags: ["Big Five traits", "EQ profile"] },
      { num: "03", word: "Grow", color: "#3AA64B", copy: "Interest mapping connects curiosity to capability, highlighting the skills worth building now and the habits that compound into strengths over the next five years.", tags: ["Interest inventory", "Skill roadmap"] },
      { num: "04", word: "Choose", color: "#F5A524", copy: "Your scores converge on a ranked shortlist of career clusters, streams and courses — each matched with fit scores, so the decision stops being a guess.", tags: ["40+ career clusters", "Fit scoring"] },
      { num: "05", word: "Succeed", color: "#E84393", copy: "Walk away with a counselor-reviewed action plan: subject choices, college pathways and milestones — a living map you return to at every crossroads.", tags: ["Counselor review", "Action plan"] },
    ],
  },
  hi: {
    tag: "तरीका",
    headA: "पाँच अध्याय। एक कहानी —",
    headB: "आपकी।",
    chapters: [
      { num: "01", word: "खोजें", color: "#1F5BFF", copy: "सावधानी से तैयार योग्यता-परीक्षण बताते हैं कि आप वास्तव में किसमें अच्छे हैं — तार्किक, भाषाई, स्थानिक, संख्यात्मक और यांत्रिक तर्क, हज़ारों साथियों के साथ तुलना करके।", tags: ["योग्यता बैटरी", "अनुकूल परीक्षण"] },
      { num: "02", word: "समझें", color: "#7C3AED", copy: "व्यक्तित्व और भावनात्मक-बुद्धिमत्ता प्रोफ़ाइल बताती है कि आप कैसे काम करते हैं — कैसे ध्यान लगाते हैं, फ़ैसला लेते हैं, सहयोग करते हैं और दबाव में प्रतिक्रिया देते हैं। कोई ठप्पा नहीं, सिर्फ़ आपके स्वयं की भाषा।", tags: ["बिग फाइव गुण", "EQ प्रोफ़ाइल"] },
      { num: "03", word: "बढ़ें", color: "#3AA64B", copy: "रुचि-मैपिंग जिज्ञासा को क्षमता से जोड़ती है — बताती है कि अभी कौन-से कौशल बनाने चाहिए और कौन-सी आदतें अगले पाँच साल में ताकत बनेंगी।", tags: ["रुचि सूची", "कौशल रोडमैप"] },
      { num: "04", word: "चुनें", color: "#F5A524", copy: "आपके स्कोर करियर क्लस्टर, स्ट्रीम और कोर्स की क्रमबद्ध सूची बनाते हैं — हर विकल्प के साथ फिट स्कोर, ताकि फ़ैसला अंदाज़ा न रहे।", tags: ["40+ करियर क्लस्टर", "फिट स्कोरिंग"] },
      { num: "05", word: "सफल हों", color: "#E84393", copy: "काउंसलर-समीक्षित एक्शन प्लान लेकर निकलें: विषय चुनाव, कॉलेज रास्ते और मील के पत्थर — एक जीवंत नक्शा जिसे आप हर मोड़ पर देख सकते हैं।", tags: ["काउंसलर समीक्षा", "एक्शन प्लान"] },
    ],
  },
};

export default function Manifesto() {
  const { lang } = useLang();
  const copy = COPY[lang];
  return (
    <section id="manifesto" className="max-w-7xl mx-auto px-5 sm:px-8 py-28 sm:py-36">
      <SectionTag color="#1F5BFF">{copy.tag}</SectionTag>
      <Reveal>
        <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight max-w-3xl text-balance leading-[1.05]">
          {copy.headA} <span className="font-serifa italic font-normal text-iqpurple">{copy.headB}</span>
        </h2>
      </Reveal>

      <div className="mt-20 space-y-0">
        {copy.chapters.map((c, i) => (
          <Reveal key={c.num} delay={0.05}>
            <div data-testid={`chapter-${c.num}`} className={`group grid md:grid-cols-12 gap-6 md:gap-10 items-center py-12 sm:py-16 border-t border-ink/10 ${i === copy.chapters.length - 1 ? "border-b" : ""}`}>
              <div className={`md:col-span-4 ${i % 2 ? "md:order-2 md:text-right" : ""}`}>
                <span className="num-outline font-display font-extrabold text-7xl sm:text-8xl leading-none transition-all duration-500" style={{ WebkitTextStrokeColor: c.color }}>{c.num}</span>
                <h3 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-3" style={{ color: c.color }}>{c.word}</h3>
              </div>
              <div className={`md:col-span-8 ${i % 2 ? "md:order-1" : ""}`}>
                <p className="text-base sm:text-lg text-ink/70 leading-relaxed max-w-2xl">{c.copy}</p>
                <div className="mt-5 flex flex-wrap gap-2.5">
                  {c.tags.map((t) => (
                    <span key={t} className="text-xs font-semibold tracking-wide uppercase px-3.5 py-1.5 rounded-full border transition-colors duration-300" style={{ borderColor: `${c.color}55`, color: c.color }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
